// A prévia dos serviços impactados por uma regra (design D13; spec rule-impact-preview),
// pedida ao servidor com DEBOUNCE e só com a forma válida: nunca por tecla. Cada pedido
// traz o `previewStamp` das entradas em que foi calculado, que o salvar devolve — se a obra
// mudou entre a prévia e a confirmação, o servidor recusa e a prévia é recalculada aqui.
//
// Estados: `idle` (nada a calcular ainda), `loading`, `ready`, `unavailable` (a obra não
// tem origem utilizável: não há o que calcular, e o motivo é declarado) e `error` (falha de
// rede ou do servidor — a tela oferece tentar de novo). Uma resposta de um pedido já
// substituído por outro nunca sobrescreve o atual.
import { useCallback, useEffect, useRef, useState } from "react";

import { previewDisbursementRuleImpact } from "./disbursementApi";

export const IMPACT_DEBOUNCE_MS = 350;

const IDLE = Object.freeze({ status: "idle", answer: null, reason: null });

/** Só os alvos de insumo, família de insumo e grupo têm prévia de impacto (spec). */
export const requiresImpactPreview = (target) =>
  target?.kind === "resource" || target?.kind === "resource_group";

const useRuleImpact = ({
  workId,
  target,
  profile,
  // A competência de início da regra: o mesmo perfil a partir de outro mês é outra regra.
  startMonth = null,
  // D21: "reference" é o ajuste mês a mês; nele o perfil pode ser `null` (só o deslocamento).
  basis = "new",
  enabled,
  debounceMs = IMPACT_DEBOUNCE_MS,
}) => {
  const [state, setState] = useState(IDLE);
  const [tick, setTick] = useState(0);
  const hasForm = profile || (basis === "reference" && profile === null);
  const inputKey =
    enabled && workId && target && hasForm
      ? JSON.stringify([workId, target.kind, target.key, target.level, basis, profile, startMonth ?? null])
      : null;
  const latest = useRef({ target, profile, startMonth, basis });
  latest.current = { target, profile, startMonth, basis: basis === "reference" ? basis : undefined };

  useEffect(() => {
    if (!inputKey) {
      setState(IDLE);
      return undefined;
    }
    let current = true;
    // Uma forma nova invalida a resposta anterior NA HORA: o carimbo dela não vale mais.
    setState({ status: "loading", answer: null, reason: null });
    const run = async () => {
      try {
        const result = await previewDisbursementRuleImpact(workId, latest.current);
        if (!current) return;
        if (result?.code === 200) {
          setState({ status: "ready", answer: result, reason: null });
        } else if (result?.errorCode === "impact_unavailable") {
          setState({ status: "unavailable", answer: null, reason: result?.details?.reason ?? null });
        } else {
          setState({ status: "error", answer: null, reason: null });
        }
      } catch {
        if (current) setState({ status: "error", answer: null, reason: null });
      }
    };
    // Sem espera (testes, ou quem já debounceou por fora) o pedido sai na hora.
    if (debounceMs <= 0) {
      run();
      return () => {
        current = false;
      };
    }
    const timer = setTimeout(run, debounceMs);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [inputKey, tick, debounceMs, workId]);

  const refresh = useCallback(() => setTick((value) => value + 1), []);
  return { ...state, refresh };
};

export default useRuleImpact;
